/* eslint-disable @typescript-eslint/naming-convention */
import { Flowrate, Nullable, SerializedMeterRegister, StrictOmit, flowrateSchema } from '@enmon/commons';
import type {
  Manufacturer,
  MeterModel,
  SerializedInventoryLocation,
  SerializedInventoryLocationBuilding,
  SerializedMeterModel,
} from '@enmon/commons/lib/tools';
import {
  CreateInventoryMeterOutputDto,
  SerializedInventoryMeterOutput,
  UpdateInventoryMeterOutputDto,
  InventoryMeterMountType,
  SerializedInventoryMeter,
} from '@enmon/commons/lib/tools';
import { RcFile } from 'antd/lib/upload';
import moment from 'moment';
import { booleanToSelectValue } from 'utils/booleanToSelectValue';
import create from 'zustand';
import { isNil, omit } from 'rambda';
import { devtools } from 'zustand/middleware';
import { MeterAccessibility, MeterPlacement, SerializedMeter } from '@enmon/commons/lib/enmon';
import { Commodity } from '@enmon/commons/lib/shared';
import { negatePositiveNumber } from '../../utils/math';
import { DEFAULT_LAT, DEFAULT_LNG } from './const';
import { createDetailStepSlice, DetailStepForm, DetailStepSlice } from './steps/detail/slice';
import { createFloorplanStepSlice, FloorplanStepForm, FloorplanStepSlice } from './steps/floorplan/slice';
import { createLocationStepSlice, LocationStepForm, LocationStepSlice } from './steps/location/slice';
import { createMapStepSlice, MapStepForm, MapStepSlice } from './steps/map/slice';
import { createReadingStepSlice, ReadingStepForm, ReadingStepRegister, ReadingStepSlice } from './steps/reading/slice';
import { getMemoizedValues } from './steps/utils/getMemoizedValues';
import type { StepMeterDetails } from './types';

type InventorizeMeterForm = LocationStepForm & MapStepForm & FloorplanStepForm & DetailStepForm & ReadingStepForm;

interface BaseStoreValues {
  isSendingData: boolean;
  queuedMetersCount?: number;
  location: SerializedInventoryLocation | null;
  locationBuildings: SerializedInventoryLocationBuilding[] | null;
}

const INITIAL_STATE: BaseStoreValues = {
  isSendingData: false,
  queuedMetersCount: undefined,
  location: null,
  locationBuildings: null,
};

export type InventorizeMeterStore = LocationStepSlice &
  DetailStepSlice &
  ReadingStepSlice &
  MapStepSlice &
  FloorplanStepSlice &
  BaseStoreValues & {
    setQueuedMetersCount: (payload: number) => void;
    reset: (memoizeValues?: boolean) => void;
    resetAll: () => void;
    setForm: (formValues: InventorizeMeterForm) => void;
    setIsSendingData: (payload: boolean) => void;
    setLocation: (payload: SerializedInventoryLocation) => void;
    setLocationBuildings: (payload: SerializedInventoryLocationBuilding[]) => void;
    resetBaseValues: () => void;
  };

export const useStore = create<InventorizeMeterStore>(
  devtools(
    (set, get) => ({
      ...INITIAL_STATE,

      ...createLocationStepSlice(set),
      ...createDetailStepSlice(set),
      ...createReadingStepSlice(set),
      ...createMapStepSlice(set),
      ...createFloorplanStepSlice(set),

      resetBaseValues: () => set(INITIAL_STATE),

      setIsSendingData: (payload: boolean) =>
        set(() => ({
          isSendingData: payload,
        })),

      setQueuedMetersCount: (count: number) => {
        set(() => ({ queuedMetersCount: count }));
      },

      setLocation: (payload: SerializedInventoryLocation) =>
        set(() => ({
          location: payload,
          lat: payload.lat ?? DEFAULT_LAT,
          lng: payload.lng ?? DEFAULT_LNG,
        })),

      setLocationBuildings: (payload: SerializedInventoryLocationBuilding[]) =>
        set(() => ({
          locationBuildings: payload,
        })),

      resetAll: () => {
        get().resetLocationStepSlice();
        get().resetDetailStepSlice();
        get().resetMapStepSlice();
        get().resetReadingStepSlice();
        get().resetFloorplanStepSlice();
        get().resetBaseValues();
      },

      reset: (memoizeValues?: boolean) => {
        const memoizedValues = memoizeValues ? getMemoizedValues(get()) : {};
        get().resetLocationStepSlice();
        get().resetDetailStepSlice();
        get().resetMapStepSlice();
        get().resetReadingStepSlice();
        get().resetFloorplanStepSlice();

        if (memoizeValues) {
          set(() => memoizedValues);
        }
      },
      setForm: (formValues: InventorizeMeterForm) => {
        set(() => formValues);
      },
    }),
    'InventorizeMeterStore',
  ),
);

type RegisterPayload = StrictOmit<ReadingStepRegister, 'fullObis' | 'readingPhotos' | 'readingCounter'> & {
  reading_counter?: number;
  obis?: SerializedMeterRegister['obis'];
  tariff?: SerializedMeterRegister['tariff'];
};

export type SelectedInventorizeMeterPayload = StrictOmit<
  SerializedInventoryMeter,
  | 'id'
  | 'submitted_at'
  | 'submitted_by'
  | 'inventory_location_building'
  | 'installed_at'
  | 'installed_by'
  | 'serviced_at'
  | 'serviced_by'
  | 'inventory_location'
  | 'createdAt'
  | 'updatedAt'
  | 'replaced_at'
  | 'inventory_meter_reading_group'
  | 'merged_at'
  | 'origin'
  | 'reviewed_at'
  | 'reviewed_by'
> &
  StrictOmit<
    SerializedMeter,
    | '_id'
    | 'inactiveThresholdHours'
    | 'note'
    | 'registers'
    | 'detail'
    | 'commodity'
    | 'name'
    | 'createdAt'
    | 'updatedAt'
    | 'establishment'
    | 'customer'
    | 'license'
    | 'toolsId'
    | 'statistics'
  > & {
    id?: SerializedInventoryMeter['id'];
    _id?: SerializedMeter['_id'];
    inventory_location_building?: SerializedInventoryLocationBuilding['id'] | null;
    meter_model?: MeterModel['id'];
    manufacturer?: Manufacturer['id'];
    reading_date?: string;
    submitted_at: string;
    submitted_by: string | null;
    detail: SerializedMeter['detail'];
    registers?: Array<RegisterPayload>;
    outputs?: Array<CreateInventoryMeterOutputDto | UpdateInventoryMeterOutputDto>;
    inventory_meter_reading_group?: number;
    commodity: Nullable<Commodity>;
    name: Nullable<string>;
    lat?: Nullable<number>;
    lng?: Nullable<number>;
  };

const parseFlowrateForForm = (flowrate: string | null | undefined): [string | undefined, number | undefined] => {
  if (!flowrate) return [undefined, undefined];

  const [unit, value] = flowrate.split(' ');
  return [unit || undefined, value ? Number(value) : undefined];
};

export const parseFlowrateForPayload = (
  flowrate?: { value: Nullable<number>; unit: Nullable<string> } | null,
): Flowrate | null => {
  const combined = `${flowrate?.value} ${flowrate?.unit}`.trim();

  if (flowrateSchema.safeParse(combined).success) return combined;

  return null;
};

export const selectInventorizeFormFromPayload = (
  enmonMeter: Nullable<SerializedMeter>,
  inventoryMeter: Nullable<SerializedInventoryMeter>,
): InventorizeMeterForm => {
  if (!enmonMeter || !inventoryMeter) {
    return {} as InventorizeMeterForm;
  }

  const {
    apiKey,
    accessibility,
    calibrated,
    detail,
    manufactured,
    name,
    owner,
    ownerType,
    placement,
    commodity,
    registers,
    serial,
    buildingFloor,
    manufacturer,
    meterModel,
  } = enmonMeter;

  const {
    inventory_location_building,
    inventory_meter_outputs,
    inventory_meter_reading_group,
    internal_supply_point_code,
    lorawan_rssi,
    meter_mount_technology,
    meter_mount_type,
    nbiot_rssi,
    new_meter,
    remote_reading_owner,
    remote_reading_serial,
    requires_manual_aks,
    tenant,
  } = inventoryMeter;

  const [lng, lat] = enmonMeter.location?.coordinates ?? [undefined, undefined];
  const [floorplanLng, floorplanLat] = enmonMeter.floorPlanLocation?.coordinates ?? [0, 0];

  const [unitFlowrate, valueFlowrate] =
    detail && 'flowrate' in detail && detail.flowrate?.split
      ? parseFlowrateForForm(detail.flowrate)
      : [undefined, undefined];

  const widthValue = detail && 'width' in detail ? detail.width : undefined;

  // Parsing of booleans from the API response
  const isDirect = detail && 'isDirect' in detail ? booleanToSelectValue(detail.isDirect) : null;
  const flange = detail && 'flange' in detail ? booleanToSelectValue(detail.flange) : null;
  const hotWater = detail && 'hotWater' in detail ? booleanToSelectValue(detail.hotWater) : null;
  const woltman = detail && 'woltman' in detail ? booleanToSelectValue(detail.woltman) : null;

  return {
    apiKey,
    // Location step
    // Api should always return a negative number
    // but we need to negate it again to get the correct value for the input
    lorawanRssi: lorawan_rssi ? lorawan_rssi * -1 : undefined,
    nbiotRssi: nbiot_rssi ? nbiot_rssi * -1 : undefined,

    buildingFloor,
    locationBuilding: inventory_location_building?.id ?? 'empty',
    submeter: booleanToSelectValue(placement === MeterPlacement.Secondary),
    newMeter: booleanToSelectValue(new_meter),
    meterMountType: meter_mount_type ?? undefined,
    meterMountTechnology: meter_mount_technology ?? undefined,
    remoteReadingOwner: remote_reading_owner ?? undefined,
    remoteReadingSerial: remote_reading_serial ?? undefined,
    internalSupplyPointCode: internal_supply_point_code ?? undefined,
    requiresManualAks: requires_manual_aks ?? undefined,
    manufacturer: manufacturer ?? undefined,
    meterModel: meterModel ?? undefined,
    commodity: commodity ?? undefined,
    monitoredEntity: name ?? undefined,
    meterOwnerType: ownerType ?? undefined,
    meterOwner: owner ?? undefined,
    meterTenant: tenant ?? undefined,

    // Map step
    lng,
    lat,

    // Floorplan step
    floorplanPosition: {
      lng: floorplanLng,
      lat: floorplanLat,
    },
    // Reading step
    readingGroup: inventory_meter_reading_group?.id,
    registers: registers?.map(register => {
      const fullObis = {
        obis: register.obis ?? undefined,
        tariff: isNil(register.tariff) ? undefined : register.tariff,
      };

      return {
        ...register,
        fullObis,
      };
    }),
    outputs:
      inventory_meter_outputs?.map(output =>
        omit(
          ['createdAt', 'updatedAt', 'inventory_meter'] satisfies Array<keyof SerializedInventoryMeterOutput>,
          output,
        ),
      ) ?? [],

    accessibility: accessibility as MeterAccessibility[],
    manufactured: manufactured ? moment().year(manufactured) : undefined,
    calibrated: calibrated ? moment().year(calibrated) : undefined,
    internalNote: undefined,
    serialNumber: serial ?? undefined,

    detail: {
      ...detail,
      width: widthValue ? { unit: 'mm', value: widthValue } : null,
      flowrate: { value: valueFlowrate, unit: unitFlowrate },
      isDirect,
      flange,
      hotWater,
      woltman,
    } as StepMeterDetails,
  };
};

/**
 * Selector for Form data object payload
 */
export const selectInventorizePayloadFromForm =
  (
    enmonMeterId: SerializedMeter['_id'] | undefined,
    inventoryMeterId: SerializedInventoryMeter['id'] | undefined,
    submittedBy: string | null,
  ) =>
  ({
    apiKey,
    buildingFloor,
    locationBuilding,
    submeter,
    newMeter,
    meterMountType,
    meterMountTechnology,
    remoteReadingOwner,
    remoteReadingSerial,
    requiresManualAks,
    meterModel,
    commodity,
    monitoredEntity,
    meterOwnerType,
    meterOwner,
    meterTenant,
    manufacturer,
    lat,
    lng,
    floorplanPosition,
    accessibility,
    manufactured,
    calibrated,
    readingDate,
    readingGroup,
    registers = [],
    outputs,
    internalNote,
    internalSupplyPointCode,
    nbiotRssi,
    lorawanRssi,
    serialNumber,
    detail,
  }: InventorizeMeterStore): SelectedInventorizeMeterPayload => ({
    submitted_at: new Date().toISOString(),
    submitted_by: submittedBy,
    id: inventoryMeterId,
    _id: enmonMeterId,

    // Location step
    apiKey,
    buildingFloor: buildingFloor ?? null,
    inventory_location_building: locationBuilding === 'empty' ? null : locationBuilding,
    placement: submeter ? MeterPlacement.Secondary : MeterPlacement.Main,
    new_meter: Boolean(newMeter),
    meter_mount_type: meterMountType ?? InventoryMeterMountType.Free,
    meter_mount_technology: meterMountTechnology ?? null,
    remote_reading_owner: remoteReadingOwner ?? null,
    remote_reading_serial: remoteReadingSerial ?? null,
    requires_manual_aks: requiresManualAks ?? false,
    meter_model: meterModel,
    commodity,
    name: monitoredEntity,
    ownerType: meterOwnerType ?? null,
    owner: meterOwner ?? null,
    tenant: meterTenant ?? null,
    manufacturer,

    // Map step
    lat: lat ?? null,
    lng: lng ?? null,

    // Floorplan step
    floorplan_lat: floorplanPosition.lat,
    floorplan_lng: floorplanPosition.lng,

    // Reading step
    outputs,
    registers: registers?.map(({ readingPhotos, readingCounter, fullObis, ...rest }) => ({
      ...rest,
      reading_counter: readingCounter,
      obis: fullObis?.obis,
      tariff: fullObis?.tariff,
    })),
    inventory_meter_reading_group: readingGroup,
    accessibility: accessibility ?? null,
    manufactured: manufactured?.year() ?? null,
    calibrated: calibrated?.year() ?? null,
    reading_date: readingDate,
    internal_note: internalNote ?? null,
    internal_supply_point_code: internalSupplyPointCode ?? null,

    // input returns the value as a positive number, but the API expects a negative number
    // input has a - sign in front of the number as a prefix
    lorawan_rssi: negatePositiveNumber(lorawanRssi) ?? null,
    nbiot_rssi: negatePositiveNumber(nbiotRssi) ?? null,

    serial: serialNumber ?? null,

    detail: {
      ...(detail ?? {}),
      ...(detail &&
        'flowrate' in detail && {
          flowrate: parseFlowrateForPayload(detail.flowrate),
        }),

      ...(detail &&
        'width' in detail && {
          width:
            detail?.width?.unit === 'DIN' ? (detail?.width?.value ?? 0) * 17.5 || null : detail?.width?.value ?? null,
        }),
      ...(detail && 'isDirect' in detail && { isDirect: Boolean(detail.isDirect) }),
      ...(detail && 'flange' in detail && { flange: detail.flange === null ? null : Boolean(detail.flange) }),
      ...(detail && 'hotWater' in detail && { hotWater: detail.hotWater === null ? null : Boolean(detail.hotWater) }),
      ...(detail && 'woltman' in detail && { woltman: detail.woltman === null ? null : Boolean(detail.woltman) }),
    },
  });

export const selectMeterModel =
  (meterModels: SerializedMeterModel[]) =>
  (state: InventorizeMeterStore): { meterModel: SerializedMeterModel | null } => ({
    meterModel: meterModels?.find(meterModel => meterModel.id === state.meterModel) ?? null,
  });

export interface SelectedInventoryPhotos {
  originPhotoContextList: (RcFile | undefined)[];
  originPhotoDetailList: (RcFile | undefined)[];
  originPhotoMeterModuleList: (RcFile | undefined)[];
  originPhotoRegisterReadingList: ((RcFile | undefined)[] | undefined)[];
}

/**
 * Selector for Form Photos
 * @param {state} state state from main useStore.
 * @returns {SelectedInventoryPhotos} object with Form Files.
 */
export const selectInventorizeFormPhotos = (state: InventorizeMeterStore): SelectedInventoryPhotos => ({
  originPhotoContextList: state.photoContextList?.map(photo => photo.originFileObj) ?? [],
  originPhotoDetailList: state.photoDetailList?.map(photo => photo.originFileObj) ?? [],
  originPhotoRegisterReadingList: state.registers
    ? state.registers.map(register => register.readingPhotos?.map(photo => photo.originFileObj) ?? [])
    : [],
  originPhotoMeterModuleList: state.photoMeterModuleList?.map(photo => photo.originFileObj) ?? [],
});
